'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useState, useSyncExternalStore, } from 'react';
import { cx } from '../../../internal/cx.js';
const UNITS = [
    // unit, seconds per unit, switch to the next unit at this many of this one
    ['second', 1, 60],
    ['minute', 60, 60],
    ['hour', 3600, 24],
    ['day', 86400, 7],
    ['week', 604800, 4.345],
    ['month', 2629800, 12],
    ['year', 31557600, Infinity],
];
function toDate(input) {
    return input instanceof Date ? input : new Date(input);
}
/**
 * Picks the largest unit that keeps the value below its rollover, after
 * rounding, so 59.6 minutes reads "1 hour ago" and not "60 minutes ago".
 */
export function relativeTimeParts(date, now = Date.now()) {
    const seconds = (toDate(date).getTime() - toDate(now).getTime()) / 1000;
    for (const [unit, size, rollover] of UNITS) {
        const value = Math.round(seconds / size);
        if (Math.abs(value) < rollover)
            return { value: value === 0 ? 0 : value, unit };
    }
    return { value: Math.round(seconds / 31557600), unit: 'year' };
}
/** Formats a date relative to `now` with `Intl.RelativeTimeFormat` ("3 hours ago", "in 2 days"). */
export function formatRelativeTime(date, options = {}) {
    const { now = Date.now(), locale, numeric = 'auto', format = 'long' } = options;
    const { value, unit } = relativeTimeParts(date, now);
    return new Intl.RelativeTimeFormat(locale, { numeric, style: format }).format(value, unit);
}
/** How often to re-render a live timestamp: often while it is fresh, rarely once it is old. */
function tickFor(date, now) {
    const age = Math.abs(now - date.getTime());
    if (age < 60_000)
        return 5_000;
    if (age < 3_600_000)
        return 30_000;
    if (age < 86_400_000)
        return 300_000;
    return 3_600_000;
}
const noopSubscribe = () => () => { };
/** False on the server and during hydration, true afterwards and on client-only mounts. */
function useHydrated() {
    return useSyncExternalStore(noopSubscribe, () => true, () => false);
}
const DEFAULT_ABSOLUTE = { dateStyle: 'medium', timeStyle: 'short' };
/**
 * A `<time datetime>` with localised relative text ("3 hours ago") and the
 * absolute time on hover and focus.
 */
export const RelativeTime = forwardRef(function RelativeTime({ date, now: nowProp, locale, timeZone, numeric = 'auto', format = 'long', absoluteFormat = DEFAULT_ABSOLUTE, absoluteAfter, live = false, tooltip = true, className, onKeyDown, onBlur, ...rest }, ref) {
    const tipId = useId();
    const injected = nowProp === undefined ? undefined : toDate(nowProp).getTime();
    // The clock is read once for the first render, so server and client agree
    // when `now` is injected; otherwise the effect below corrects it after mount.
    const [clock, setClock] = useState(() => injected ?? Date.now());
    const [dismissed, setDismissed] = useState(false);
    const hydrated = useHydrated();
    const now = injected ?? clock;
    const when = toDate(date);
    const valid = !Number.isNaN(when.getTime());
    useEffect(() => {
        if (injected === undefined)
            setClock(Date.now());
    }, [injected, hydrated]);
    useEffect(() => {
        if (!live || injected !== undefined || !valid)
            return;
        const delay = typeof live === 'number' ? live : tickFor(when, Date.now());
        const timer = setTimeout(() => setClock(Date.now()), delay);
        return () => clearTimeout(timer);
        // Re-arms after every tick because `clock` changes.
    }, [live, injected, valid, when.getTime(), clock]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!valid) {
        return (_jsx("time", { ref: ref, ...rest, className: cx('mn-relative-time', className), "data-invalid": "", children: "\u2014" }));
    }
    const absolute = new Intl.DateTimeFormat(locale, {
        ...absoluteFormat,
        ...(timeZone ? { timeZone } : {}),
    }).format(when);
    const useAbsolute = absoluteAfter !== undefined && Math.abs(now - when.getTime()) > absoluteAfter;
    const text = useAbsolute
        ? absolute
        : formatRelativeTime(when, { now, numeric, format, ...(locale ? { locale } : {}) });
    const showTip = tooltip && !useAbsolute;
    return (_jsxs("span", { className: cx('mn-relative-time', className), "data-dismissed": dismissed || undefined, children: [_jsx("time", { ref: ref, ...rest, className: "mn-relative-time__text", dateTime: when.toISOString(), tabIndex: showTip ? 0 : rest.tabIndex, "aria-describedby": showTip ? tipId : rest['aria-describedby'], suppressHydrationWarning: true, onKeyDown: (event) => {
                    if (event.key === 'Escape' && showTip)
                        setDismissed(true);
                    onKeyDown?.(event);
                }, onBlur: (event) => {
                    setDismissed(false);
                    onBlur?.(event);
                }, onPointerLeave: () => setDismissed(false), children: text }), showTip ? (_jsx("span", { id: tipId, role: "tooltip", className: "mn-relative-time__tip", suppressHydrationWarning: true, children: absolute })) : null] }, hydrated ? 'client' : 'server'));
});
//# sourceMappingURL=relative-time.js.map